import { useState } from 'react';

import { cn } from '@/lib/utils';

export interface PieSlice {
  key: string;
  label: string;
  value: number;
  color: string;
  /** Texto secundario opcional para el tooltip / hover (p. ej. duración). */
  hint?: string;
}

interface Props {
  slices: PieSlice[];
  size?: number;
  /** Texto central (p. ej. total). Si se omite, no se dibuja. */
  centerLabel?: string;
  centerHint?: string;
  ariaLabel?: string;
}

/**
 * Pie chart SVG inline sin dependencias. Cálculo de arcos a mano.
 *
 * - Soporta segmento único (100%) renderizado como círculo entero.
 * - Soporta 0 datos: dibuja un círculo gris con texto "Sin datos".
 * - Hover de segmento: resalta y muestra label + value en el centro.
 */
export function PieChart({
  slices,
  size = 220,
  centerLabel,
  centerHint,
  ariaLabel,
}: Props) {
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  const total = slices.reduce((acc, s) => acc + s.value, 0);
  const cx = size / 2;
  const cy = size / 2;
  const radius = (size / 2) * 0.92;
  const innerRadius = radius * 0.55; // donut

  if (total <= 0) {
    return (
      <div className="flex flex-col items-center">
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          aria-label={ariaLabel ?? 'Sin datos'}
          role="img"
        >
          <circle cx={cx} cy={cy} r={radius} className="fill-paper stroke-border" strokeWidth={1} />
          <circle cx={cx} cy={cy} r={innerRadius} className="fill-card" />
          <text
            x={cx}
            y={cy + 4}
            textAnchor="middle"
            className="fill-muted-foreground"
            style={{ fontSize: 12, fontFamily: 'inherit' }}
          >
            Sin datos
          </text>
        </svg>
      </div>
    );
  }

  // Slice de 100%: SVG path con dos arcos no funciona (start == end).
  if (slices.length === 1 || slices.filter((s) => s.value > 0).length === 1) {
    const only = slices.find((s) => s.value > 0)!;
    return (
      <div className="flex flex-col items-center">
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          aria-label={ariaLabel ?? `${only.label} 100%`}
          role="img"
        >
          <circle cx={cx} cy={cy} r={radius} fill={only.color} />
          <circle cx={cx} cy={cy} r={innerRadius} className="fill-card" />
          <CenterText
            cx={cx}
            cy={cy}
            label={centerLabel}
            hint={centerHint}
          />
        </svg>
      </div>
    );
  }

  let angle = -Math.PI / 2; // empieza arriba
  const arcs = slices
    .filter((s) => s.value > 0)
    .map((slice) => {
      const sliceAngle = (slice.value / total) * Math.PI * 2;
      const start = angle;
      const end = angle + sliceAngle;
      angle = end;
      const largeArc = sliceAngle > Math.PI ? 1 : 0;
      const sx = cx + radius * Math.cos(start);
      const sy = cy + radius * Math.sin(start);
      const ex = cx + radius * Math.cos(end);
      const ey = cy + radius * Math.sin(end);
      const isx = cx + innerRadius * Math.cos(end);
      const isy = cy + innerRadius * Math.sin(end);
      const iex = cx + innerRadius * Math.cos(start);
      const iey = cy + innerRadius * Math.sin(start);
      const path = [
        `M ${sx} ${sy}`,
        `A ${radius} ${radius} 0 ${largeArc} 1 ${ex} ${ey}`,
        `L ${isx} ${isy}`,
        `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${iex} ${iey}`,
        'Z',
      ].join(' ');
      return { slice, path };
    });

  const hovered = hoverKey ? slices.find((s) => s.key === hoverKey) ?? null : null;

  return (
    <div className="flex flex-col items-center">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-label={ariaLabel ?? 'Distribución'}
        role="img"
      >
        {arcs.map(({ slice, path }) => (
          <path
            key={slice.key}
            d={path}
            fill={slice.color}
            className={cn(
              'transition-opacity duration-150 cursor-pointer',
              hoverKey && hoverKey !== slice.key ? 'opacity-40' : 'opacity-100',
            )}
            onMouseEnter={() => setHoverKey(slice.key)}
            onMouseLeave={() => setHoverKey(null)}
          >
            <title>
              {slice.label}
              {slice.hint ? ` · ${slice.hint}` : ''}
            </title>
          </path>
        ))}
        <CenterText
          cx={cx}
          cy={cy}
          label={hovered?.label ?? centerLabel}
          hint={hovered?.hint ?? centerHint}
        />
      </svg>
    </div>
  );
}

function CenterText({
  cx,
  cy,
  label,
  hint,
}: {
  cx: number;
  cy: number;
  label?: string;
  hint?: string;
}) {
  if (!label && !hint) return null;
  return (
    <g>
      {label ? (
        <text
          x={cx}
          y={hint ? cy - 4 : cy + 5}
          textAnchor="middle"
          className="fill-foreground font-mono font-semibold tabular-nums"
          style={{ fontSize: 18, fontFamily: 'inherit' }}
        >
          {label}
        </text>
      ) : null}
      {hint ? (
        <text
          x={cx}
          y={cy + 14}
          textAnchor="middle"
          className="fill-muted-foreground"
          style={{ fontSize: 11, fontFamily: 'inherit', letterSpacing: '0.04em' }}
        >
          {hint}
        </text>
      ) : null}
    </g>
  );
}
